import {
  FormControl,
  FormControlProps,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  useMergeRefs,
} from "@chakra-ui/react";
import { dataAttr } from "@chakra-ui/utils";
import React, { useMemo } from "react";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import { InputType } from "shared/components/form/InputContent";
import { InputElementButton } from "shared/components/input/InputElementButton";
import { SimpleInputErrorElement } from "shared/components/input/select/SimpleInputErrorElement";
import { InputXMarkIcon } from "shared/icons/InputXMarkIcon";
import { getFieldError } from "shared/utils/FormUtils";

export interface SimpleTextInputProps extends InputProps {
  error?: string;
  isNotApplicable?: boolean;
  inputLeftElement?: React.ReactNode;
  inputRightElement?: React.ReactNode;
  controlProps?: FormControlProps;
  onChangeValue?: (value: string) => void;
  onClear?: () => void;
  inputType?: InputType;
  isEditing?: boolean;
}

export type FormSimpleTextInputProps = {
  name: string;
  shouldUnregister?: boolean;
} & Omit<SimpleTextInputProps, "name" | "value" | "error" | "onChange">;

export const SimpleTextInput = React.forwardRef<
  HTMLInputElement,
  SimpleTextInputProps
>(
  (
    {
      error,
      isNotApplicable,
      isDisabled,
      inputLeftElement,
      inputRightElement: inputRightElementProp,
      controlProps,
      size,
      onChangeValue,
      onClear,
      type = "text",
      inputType,
      isEditing,
      children,
      variant = "simple",
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation(["common"]);
    const inputRightElement = useMemo(() => {
      if (inputRightElementProp) {
        return inputRightElementProp;
      }
      if (error) {
        return <SimpleInputErrorElement error={error} />;
      }
      if (onClear && props.value) {
        return (
          <InputElementButton
            className="mb-input-clear"
            aria-label={t("common:close")}
            title={t("common:close")}
            icon={
              <InputXMarkIcon
                className="mb-input-clear-icon"
                color="gray.400"
              />
            }
            onClick={onClear}
          />
        );
      }
    }, [error, inputRightElementProp, onClear, props.value, t]);
    return (
      <FormControl
        my={0}
        isDisabled={isDisabled}
        isInvalid={!!error}
        variant={variant}
        {...controlProps}
      >
        <InputGroup variant={variant} size={size}>
          {inputLeftElement && (
            <InputLeftElement
              data-disabled={dataAttr(isDisabled)}
              data-na={dataAttr(isNotApplicable)}
            >
              {inputLeftElement}
            </InputLeftElement>
          )}
          <Input
            ref={ref}
            autoComplete="off"
            autoCorrect="off"
            isInvalid={!!error}
            isDisabled={isDisabled || isNotApplicable}
            data-blank={dataAttr(!props.value)}
            data-na={dataAttr(isNotApplicable)}
            data-input-type={inputType}
            data-editing={dataAttr(isEditing)}
            min={0}
            type={type}
            size={size}
            variant={variant}
            width="100%"
            onChange={(event) => {
              props.onChange?.(event);
              if (event.isPropagationStopped()) {
                return;
              }
              onChangeValue?.(event.target.value);
            }}
            onKeyDown={(event) => {
              props.onKeyDown?.(event);
              if (event.isPropagationStopped()) {
                return;
              }
              if (event.key === "Escape") {
                onClear?.();
              }
            }}
            {...props}
          />
          {inputRightElement && (
            <InputRightElement h="100%">{inputRightElement}</InputRightElement>
          )}
          {children}
        </InputGroup>
      </FormControl>
    );
  },
);

SimpleTextInput.displayName = "SimpleTextInput";

export const FormSimpleTextInput = React.forwardRef<
  HTMLInputElement,
  FormSimpleTextInputProps
>(
  (
    {
      name,
      isDisabled,
      defaultValue = "",
      shouldUnregister = true,
      ...otherProps
    },
    fwRef,
  ) => {
    const prefixedName = usePrefixedFieldName(name);
    const {
      field: { ref: fieldRef, ...field },
      formState: { errors },
    } = useController({
      name: prefixedName,
      shouldUnregister,
      defaultValue,
    });
    const mergedRef = useMergeRefs(fieldRef, fwRef);
    const isFormDisabled = useIsFormDisabled();
    return (
      <SimpleTextInput
        {...field}
        {...otherProps}
        isDisabled={isDisabled || isFormDisabled}
        error={getFieldError(errors, prefixedName)?.message}
        ref={mergedRef}
      />
    );
  },
);
FormSimpleTextInput.displayName = "FormSimpleTextInput";
